import { useCallback, useState } from 'react';
import { ExternalLink } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useDismiss, autoUpdate, offset } from '@floating-ui/react';
import { PopperWrapper } from '@trinity/chat-components/components/Popper';
import { type BuildMapLinkParams } from '../../lib';
import type { MapsLinkResult } from './hooks/useGetMapTargets';
import { useMapDirectionsLinks, useMapAddressLinks } from './hooks/useGetMapTargets';
import { BaseMessageButton } from './Message.Button';

type Props = {
  children: React.ReactNode;
  mapParams: BuildMapLinkParams;
};

export const OpenMapsButton = ({ mapParams, children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const mapLinks = useMapDirectionsLinks({ mapParams });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right',
    whileElementsMounted: autoUpdate,
    middleware: [offset(10)],
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  if (mapLinks.length === 1) {
    return (
      <a href={mapLinks[0].mapsLink} target="_blank" rel="noreferrer">
        <GetDirectionsButton>
          {children}
        </GetDirectionsButton>
      </a>
    );
  }

  return (
    <>
      <GetDirectionsButton ref={refs.setReference} onClick={() => setIsOpen(old => !old)} {...getReferenceProps()}>
        {children}
      </GetDirectionsButton>
      {isOpen && <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
        <PopperContent mapLinks={mapLinks} />
      </div>
      }
    </>
  );
};

const GetDirectionsButton = styled(BaseMessageButton)(() => ({
  marginTop: 15,
}));

type AddressProps = {
  address: string;
};

export const OpenAddressButton = ({ address }: AddressProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const mapLinks = useMapAddressLinks({ address });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right',
    whileElementsMounted: autoUpdate,
    middleware: [offset(10)],
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  if (mapLinks.length === 1) {
    return (
      <StyledAddressLink href={mapLinks[0].mapsLink} target="_blank" rel="noreferrer">
        <AddressLinkText>{address}</AddressLinkText>
        <StyledLinkIcon />
      </StyledAddressLink>
    );
  }

  return (
    <>
      <StyledAddressLink ref={refs.setReference} onClick={() => setIsOpen(old => !old)} {...getReferenceProps()}>
        <AddressLinkText>{address}</AddressLinkText>
        <StyledLinkIcon />
      </StyledAddressLink>
      {isOpen && <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
        <PopperContent mapLinks={mapLinks} onClick={() => setIsOpen(false)} />
      </div>
      }
    </>
  );
};

type PopperContentProps = {
  mapLinks: MapsLinkResult[];
  onClick?: () => void;
};

const PopperContent = (props: PopperContentProps) => {
  const [clickedLinks, setClickedLinks] = useState<MapsLinkResult[]>([]);
  const onClick = useCallback((link: MapsLinkResult) => {
    if (!clickedLinks.includes(link)) {
      setClickedLinks([...clickedLinks, link]);
    }
    props.onClick?.();
  }, [clickedLinks, props]);

  return (
    <PopperWrapper>
      <MapsIconContainer>
        {props.mapLinks.map((link, index) => (
          <div key={index}>
            <a href={link.mapsLink} target="_blank" rel="noreferrer" onClick={() => onClick(link)}>
              <MapsIcon src={link.icon} alt="Open in app" />
            </a>
            <MapTitle>{link.title}</MapTitle>
            {clickedLinks.includes(link) && link.appStoreLink && (
              <AppLink href={link.appStoreLink} target="_blank" rel="noreferrer">Download</AppLink>
            )}
          </div>
        ))}
      </MapsIconContainer>
    </PopperWrapper>
  );
};

const MapsIconContainer = styled.div({
  display: 'flex',
  gap: 10,
  padding: 10,
});

const MapsIcon = styled.img(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: 8,
  border: `1px solid ${theme.palette.gray.light1}`,
}));

const MapTitle = styled.div({
  textAlign: 'center',
  fontSize: 12,
  marginTop: 5,
});

const AppLink = styled.a({
  marginTop: 5,
  fontSize: 12,
  textAlign: 'center',
});

const StyledAddressLink = styled.a(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
  overflow: 'hidden',
  height: '100%',
  cursor: 'pointer',

  ':hover': {
    color: theme.palette.hyperlink,
  },

  [`> ${String(StyledLinkIcon)}`]: {
    opacity: 0,
  },

  [`:hover > ${String(StyledLinkIcon)}`]: {
    opacity: 1,
  },
}));

const AddressLinkText = styled.div({
  height: '100%',
});

const StyledLinkIcon = styled(ExternalLink)(({ theme }) => ({
  color: theme.palette.hyperlink,
  transition: 'all 150ms',
  flexShrink: 0,
  marginLeft: 4,
  height: 16,
}));